import type { InfiniteData } from "@tanstack/react-query";
import { isCfCatalogEligible } from "@/modules/cf-feed/domain/lib/catalog-eligibility";
import type {
  AdminCatalogItem,
  CatalogListResponse,
  SessionProductItem,
} from "@/modules/cf-feed/presentation/api/types";

export type CatalogCacheData =
  | CatalogListResponse
  | InfiniteData<CatalogListResponse>;

/** อัปเดตทั้งแบบหน้าเดียว/หลายหน้า รวมถึง variant ในกลุ่ม โดยไม่ทิ้ง pageParams */
export function updateCatalogCacheAfterAdd(
  cached: CatalogCacheData | undefined,
  added: SessionProductItem[],
): CatalogCacheData | undefined {
  if (!cached) return cached;
  const byVariant = new Map(added.map((item) => [item.variantId, item]));
  const updateItem = (item: AdminCatalogItem): AdminCatalogItem => {
    const saved = byVariant.get(item.variantId);
    return saved
      ? {
          ...item,
          allocatedByOtherPosts: saved.allocatedByOtherPosts,
          availableToAdd: saved.availableToAllocate,
          stockOnHand: saved.stockOnHand,
          defaultCfCode: saved.cfCode,
          suggestedCfCode: saved.cfCode,
        }
      : item;
  };
  const updatePage = (page: CatalogListResponse): CatalogListResponse => ({
    ...page,
    items: page.items.map(updateItem),
    products: page.products?.map((product) => ({
      ...product,
      variants: product.variants.map(updateItem),
    })),
  });
  return "pages" in cached
    ? { ...cached, pages: cached.pages.map(updatePage) }
    : updatePage(cached);
}

export { isCfCatalogEligible };

export function isCatalogItemEligibleForAdd(item: AdminCatalogItem): boolean {
  return (
    isCfCatalogEligible(item) && Number.isFinite(item.price) && item.price > 0
  );
}

/** ให้ server เลือกจำนวนล่าสุดจาก stock authority เพื่อไม่ใช้ค่า catalog ที่อาจ stale */
export function buildCatalogAddInput(item: AdminCatalogItem): {
  variantId: string;
} {
  return { variantId: item.variantId };
}

/** ກວດວ່າເພີ່ມຈາກ catalog ໄດ້ — preorder ບໍ່ຕ້ອງມີ stock */
export function canAddCatalogItem(
  item: AdminCatalogItem,
  input: {
    hasFeed: boolean;
    inPostVariantIds: Set<string>;
    isAdding: boolean;
  },
): boolean {
  return canSelectCatalogItem(item, input);
}

export function canSelectCatalogItem(
  item: AdminCatalogItem,
  input: {
    hasFeed: boolean;
    inPostVariantIds: Set<string>;
    isAdding: boolean;
    isItemEligible?: (item: AdminCatalogItem) => boolean;
  },
): boolean {
  return (
    input.hasFeed &&
    !input.inPostVariantIds.has(item.variantId) &&
    !input.isAdding &&
    (input.isItemEligible?.(item) ?? isCatalogItemEligibleForAdd(item))
  );
}

export async function addCatalogItemsBestEffort<T>(
  items: T[],
  add: (item: T) => Promise<void>,
): Promise<{ added: T[]; failed: T[] }> {
  const added: T[] = [];
  const failed: T[] = [];

  for (const item of items) {
    try {
      await add(item);
      added.push(item);
    } catch {
      failed.push(item);
    }
  }

  return { added, failed };
}

export function updateCatalogSelection(
  selectedIds: Set<string>,
  selectableIds: Iterable<string>,
  selected: boolean,
): Set<string> {
  const next = new Set(selectedIds);
  for (const id of selectableIds) {
    if (selected) next.add(id);
    else next.delete(id);
  }
  return next;
}

export async function collectCatalogItemsByPage<T>(
  loadPage: (
    offset: number,
    limit: number,
  ) => Promise<{ items: T[]; total: number; pageSize: number }>,
  limit = 100,
): Promise<T[]> {
  const items: T[] = [];
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;

  while (offset < total) {
    const page = await loadPage(offset, limit);
    items.push(...page.items);
    total = Math.max(0, page.total);
    if (page.pageSize <= 0) break;
    offset += page.pageSize;
  }

  return items;
}
